import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import {
  ActionFunction,
  Outlet,
  useOutletContext,
  useLoaderData,
  useFetcher,
  useNavigate,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import FormTabNavigation, {
  FormTab,
} from 'components/layout/FormTabNavigation';
import { ROUTES, routeWithParams } from 'router/routes';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import {
  FormMode,
  PanelRootContext,
  TraxeroActionResponse,
} from 'types/PanelRootContext';
import {
  createSystem,
  createTenant,
  createTenantGroups,
  updateSystem,
  updateTenant,
  updateTenantGroup,
  getTopsPPIAdapterServiceHealth,
  getFileServiceHealth,
  getFindMyCarServiceHealth,
  getJobServiceHealth,
  getNotificationServiceHealth,
  getPermitClientServiceHealth,
  getPermitServiceHealth,
  getPropertyServiceHealth,
  getQRCodeServiceHealth,
  getRedirectServiceHealth,
  getRevisionServiceHealth,
  getPaymentServiceHealth,
  getSystems,
  getTenantGroups,
  getTenants,
  getUserServiceHealth,
  getVehicleServiceHealth,
} from 'api';
import { ServiceStatus, System, Tenant, TenantGroup } from 'types';
import AuthService from 'utils/AuthService';
import { logError } from 'utils';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { TOAST_TYPE } from 'context/toastContext';
import { ToastContext } from 'context/toastContext';

export interface SuperLoaderData {
  file: Promise<{ status: ServiceStatus }>;
  user: Promise<{ status: ServiceStatus }>;
  vehicle: Promise<{ status: ServiceStatus }>;
  property: Promise<{ status: ServiceStatus }>;
  job: Promise<{ status: ServiceStatus }>;
  revision: Promise<{ status: ServiceStatus }>;
  permit: Promise<{ status: ServiceStatus }>;
  notification: Promise<{ status: ServiceStatus }>;
  redirect: Promise<{ status: ServiceStatus }>;
  findMyCar: Promise<{ status: ServiceStatus }>;
  permitClient: Promise<{ status: ServiceStatus }>;
  payment: Promise<{ status: ServiceStatus }>;
  qrCode: Promise<{ status: ServiceStatus }>;
  topsPPIAdapterServiceStatus: Promise<{ status: ServiceStatus }>;
  _tenants: Promise<Tenant[]>;
  _tenantGroups: Promise<TenantGroup[]>;
  _systems: Promise<System[]>;
}

export interface CommandCenterContext {
  tenants: Tenant[];
  tenantGroups: TenantGroup[];
  systems: System[];
  formMode: FormMode;
  setFormMode: (formMode: FormMode) => void;
  fetcherKey: string;
  fileServiceStatus: Promise<{ status: ServiceStatus }>;
  userServiceStatus: Promise<{ status: ServiceStatus }>;
  vehicleServiceStatus: Promise<{ status: ServiceStatus }>;
  propertyServiceStatus: Promise<{ status: ServiceStatus }>;
  jobServiceStatus: Promise<{ status: ServiceStatus }>;
  revisionServiceStatus: Promise<{ status: ServiceStatus }>;
  permitServiceStatus: Promise<{ status: ServiceStatus }>;
  notificationServiceStatus: Promise<{ status: ServiceStatus }>;
  redirectServiceStatus: Promise<{ status: ServiceStatus }>;
  findMyCarServiceStatus: Promise<{ status: ServiceStatus }>;
  permitClientServiceStatus: Promise<{ status: ServiceStatus }>;
  paymentServiceStatus: Promise<{ status: ServiceStatus }>;
  qrCodeServiceStatus: Promise<{ status: ServiceStatus }>;
  topsPPIAdapterServiceStatus: Promise<{ status: ServiceStatus }>;
}

const commandCenterLoader = (): SuperLoaderData => {
  if (!AuthService.IsSuperAdmin()) {
    throw new Error('403');
  }

  return {
    file: getFileServiceHealth(),
    user: getUserServiceHealth(),
    vehicle: getVehicleServiceHealth(),
    property: getPropertyServiceHealth(),
    job: getJobServiceHealth(),
    revision: getRevisionServiceHealth(),
    permit: getPermitServiceHealth(),
    notification: getNotificationServiceHealth(),
    redirect: getRedirectServiceHealth(),
    findMyCar: getFindMyCarServiceHealth(),
    permitClient: getPermitClientServiceHealth(),
    payment: getPaymentServiceHealth(),
    qrCode: getQRCodeServiceHealth(),
    topsPPIAdapterServiceStatus: getTopsPPIAdapterServiceHealth(),
    _tenants: getTenants(false),
    _tenantGroups: getTenantGroups(),
    _systems: getSystems(),
  };
};

export const Component = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const displayToast = useContext(ToastContext);
  const {
    file,
    user,
    vehicle,
    property,
    job,
    revision,
    permit,
    notification,
    topsPPIAdapterServiceStatus,
    redirect,
    findMyCar,
    permitClient,
    payment,
    qrCode,
    _tenants,
    _tenantGroups,
    _systems,
  } = useLoaderData() as SuperLoaderData;
  const { formMode, setFormMode } =
    useOutletContext<PanelRootContext<Tenant>>();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [systems, setSystems] = useState<System[]>([]);

  useEffect(() => {
    const awaitLoaders = async () => {
      const tenants = await _tenants;
      const tenantGroups = await _tenantGroups;
      const systems = await _systems;
      setTenants(tenants);
      setTenantGroups(tenantGroups);
      setSystems(systems);
    };
    awaitLoaders();
  }, [_tenants, _tenantGroups, _systems]);

  const handleSuccess = useCallback(
    (response: TraxeroActionResponse<SuperActionHandler>) => {
      switch (response.handler) {
        case SuperActionHandler.DELETE_TENANT: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('delete_success', {
              name: `${t('tenant.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          break;
        }
        case SuperActionHandler.CREATE_TENANT: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('create_success', {
              name: `${t('tenant.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          if (
            response.body &&
            !state.userTenants.some((t) => t.id === response.body.id)
          ) {
            dispatch({
              type: ACTION_TYPE.SET_USER_TENANTS,
              payload: [...state.userTenants, response.body],
            });
          }
          navigate(routeWithParams(ROUTES.TENANTS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        case SuperActionHandler.UPDATE_TENANT: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('update_success', {
              ns: 'toasts',
              name: `${t('tenant.label', { ns: 'entities' })}`,
            }),
          });
          navigate(routeWithParams(ROUTES.TENANTS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        case SuperActionHandler.CREATE_TENANT_GROUP: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('create_success', {
              name: `${t('tenant_group.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          navigate(routeWithParams(ROUTES.TENANT_GROUPS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        case SuperActionHandler.UPDATE_TENANT_GROUP: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('update_success', {
              ns: 'toasts',
              name: `${t('tenant_group.label', { ns: 'entities' })}`,
            }),
          });
          navigate(routeWithParams(ROUTES.TENANT_GROUPS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        case SuperActionHandler.CREATE_SYSTEM: {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('create_success', {
              name: `${t('system.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          navigate(routeWithParams(ROUTES.SYSTEMS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        case SuperActionHandler.UPDATE_SYSTEM: {
          const system = JSON.parse(response.body) as System;
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('update_success', {
              ns: 'toasts',
              name: `${t('system.label', { ns: 'entities' })}`,
            }),
          });
          if (state.tenant.systemID === system.id) {
            dispatch({
              type: ACTION_TYPE.SET_FEATURES,
              payload: {
                ...state.features,
                allowJobCancellation: system.data?.ppiFeatureFlags?.allowJobCancellation || false,
              },
            })
          }
          navigate(routeWithParams(ROUTES.SYSTEMS, [state.tenant.id]), {
            replace: true,
          });
          break;
        }
        default:
          break;
      }
    },
    [state.userTenants, dispatch, navigate, state.tenant.id, t],
  );

  const handleError = useCallback(
    (response: TraxeroActionResponse<SuperActionHandler>) => {
      switch (response.handler) {
        case SuperActionHandler.DELETE_TENANT: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('delete_error', {
              name: `${t('tenant.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          break;
        }
        case SuperActionHandler.CREATE_TENANT: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('create_error', {
              name: `${t('tenant.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          break;
        }
        case SuperActionHandler.UPDATE_TENANT: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('update_error', {
              ns: 'toasts',
              name: `${t('tenant.label', { ns: 'entities' })}`,
            }),
          });
          break;
        }
        case SuperActionHandler.CREATE_TENANT_GROUP: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('create_error', {
              name: `${t('tenant_group.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          break;
        }
        case SuperActionHandler.UPDATE_TENANT_GROUP: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('update_error', {
              ns: 'toasts',
              name: `${t('tenant_group.label', { ns: 'entities' })}`,
            }),
          });
          break;
        }
        case SuperActionHandler.CREATE_SYSTEM: {
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: t('create_error', {
              name: `${t('system.label', { ns: 'entities' })}`,
              ns: 'toasts',
            }),
          });
          break;
        }
        case SuperActionHandler.UPDATE_SYSTEM: {
          if (response.status === 401) {
            displayToast({
              type: TOAST_TYPE.ERROR,
              message: t('toast.only_the_one', {
                ns: 'commandCenter',
                name: `${t('system.label', { ns: 'entities' })}`,
              }),
            });
            return;
          } else {
            displayToast({
              type: TOAST_TYPE.ERROR,
              message: t('update_error', {
                ns: 'toasts',
                name: `${t('system.label', { ns: 'entities' })} ${response.status}`,
              }),
            });
          }
          break;
        }
      }
    },
    [t],
  );

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<SuperActionHandler>;
      if (response.status < 400) {
        handleSuccess(response);
        setFormMode('none');
      } else {
        handleError(response);
      }
    }
  }, [fetcher.data]);

  const tabs: FormTab[] = useMemo(() => {
      const _tabs: FormTab[] = [
      {
        label: `${t('tabs.health' + (isMobile ? '_mobile' : ''), { ns: 'commandCenter' })}`,
        route: routeWithParams(ROUTES.HEALTH, [state.tenant.id]),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      },
      {
        label: `${t('tabs.tenants', { ns: 'commandCenter' })}`,
        route: routeWithParams(ROUTES.TENANTS, [state.tenant.id]),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      },
      {
        label: `${t('tabs.tenantGroups', { ns: 'commandCenter' })}`,
        route: routeWithParams(ROUTES.TENANT_GROUPS, [state.tenant.id]),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      },
      {
        label: `${t('tabs.systems', { ns: 'commandCenter' })}`,
        route: routeWithParams(ROUTES.SYSTEMS, [state.tenant.id]),
        beforeNavigate: () => {
          setFormMode('none');
          return true;
        },
      }]
      if (!isMobile) {
        _tabs.push({
          label: `${t('tabs.importWizard', { ns: 'commandCenter' })}`,
          route: routeWithParams(ROUTES.PROPERTY_IMPORT, [state.tenant.id]),
          beforeNavigate: () => {
            setFormMode('none');
            return true;
          },
        });
      }
      return _tabs;
    },
    [state.tenant.id],
  );

  return (
    <Box
      flexGrow={1}
      display={'flex'}
      flexDirection={'column'}
      minHeight={0}
      sx={{
        overflow: 'hidden',
      }}
    >
      <FormHeaderBar />
      {formMode === 'none' && (
        <FormTabNavigation
          tabs={tabs}
          title={t('title', { ns: 'commandCenter' })}
        />
      )}
      <Box
        flexGrow={1}
        minHeight={0}
        sx={{
          overflow: isMobile ? 'auto' : 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Outlet
          context={
            {
              formMode,
              setFormMode,
              fetcherKey,
              tenants,
              tenantGroups,
              systems,
              fileServiceStatus: file,
              userServiceStatus: user,
              vehicleServiceStatus: vehicle,
              propertyServiceStatus: property,
              jobServiceStatus: job,
              revisionServiceStatus: revision,
              permitServiceStatus: permit,
              notificationServiceStatus: notification,
              topsPPIAdapterServiceStatus: topsPPIAdapterServiceStatus,
              redirectServiceStatus: redirect,
              findMyCarServiceStatus: findMyCar,
              permitClientServiceStatus: permitClient,
              paymentServiceStatus: payment,
              qrCodeServiceStatus: qrCode,
            } satisfies CommandCenterContext
          }
        />
      </Box>
    </Box>
  );
};

export enum SuperActionHandler {
  UPDATE_SYSTEM = 'system updated',
  CREATE_SYSTEM = 'system created',
  UPDATE_TENANT = 'tenant updated',
  CREATE_TENANT = 'tenant created',
  DELETE_TENANT = 'tenant deleted',
  UPDATE_TENANT_GROUP = 'tenant group updated',
  CREATE_TENANT_GROUP = 'tenant group created',
  UNKNOWN = 'unknown handler',
}

export interface SuperActionResponse {
  error: Error | null;
  handler: SuperActionHandler;
  tenant?: Tenant;
  system?: System;
}

const commandCenterAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<SuperActionResponse>> => {
  const { handler, data, tenantID, systemID, tenantGroupID } =
    await request.json();
  let res: Response;

  try {
    switch (handler) {
      case SuperActionHandler.DELETE_TENANT:
      case SuperActionHandler.UPDATE_TENANT:
        res = await updateTenant({ body: data, tenantID });
        break;
      case SuperActionHandler.CREATE_TENANT:
        res = await createTenant({ body: data, tenantGroupID });
        break;
      case SuperActionHandler.CREATE_TENANT_GROUP:
        res = await createTenantGroups(data);
        break;
      case SuperActionHandler.UPDATE_TENANT_GROUP:
        res = await updateTenantGroup(data);
        break;
      case SuperActionHandler.UPDATE_SYSTEM:
        res = await updateSystem({ body: data, systemID });
        
        break;
      case SuperActionHandler.CREATE_SYSTEM:
        res = await createSystem(data);
        break;
      default:
        throw new Error('Invalid handler');
    }
  } catch (error) {
    if (error instanceof Error) {
      logError(error); // now TypeScript knows this is actually an Error
    } else {
      logError(new Error(String(error))); // convert non-Error objects to Error
    }
    return {
      handler,
      status: 500,
      statusText: 'Internal Server Error',
    };
  }

  if (handler === SuperActionHandler.CREATE_TENANT) {
    return {
      handler,
      status: res.status,
      statusText: res.statusText,
    };
  }

  if (handler === SuperActionHandler.UPDATE_SYSTEM) {
    return {
      handler,
      status: res.status,
      statusText: res.statusText,
      body: data as System,
    };
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};

Component.displayName = 'CommandCenterLayout';
export { commandCenterLoader as loader };
export { commandCenterAction as action };
export default Component;
